import { LoaderFunction } from 'react-router-dom';
import { AuthContextType } from '../../../../hooks/useAuth';
import { tableIds, takeBase } from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';
import { client } from '../../../../gql/client';
import { GET_SURVEILLANCE_ROUTES } from '../../../../gql/queries/surveillanceRoute';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import {
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../gql/queries/orders/predefinedData';
import { FETCH_TEAMS, FETCH_USERS } from '../../../../gql/queries/users';
import { GET_ROADS } from '../../../../gql/queries/geoPositions/roads';
import {
  selectOrdersForSurveillanceRoutes,
  SurveillanceRouteOrder,
} from '../../../../gql/selectors/surveillanceRoute';
import { warmupOrdersByIds } from '../../../../gql/selectors/warmupOrders';
import { GET_ORDER_BY_ID } from '../../../../gql/queries/orders/orders';
import type { DocumentNode } from '@apollo/client';
import type { SurveillanceRoute } from '../../../../types/surveillanceRoute';
import { store } from '../../../../state/configureStore';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { getCursor } from '../../../../state/selectors/ui/tables';
import {
  currentSurveillanceRouteListVersion,
  isSurveillanceRouteListStale,
  markSurveillanceRouteListFresh,
} from '../../../../gql/surveillanceRouteListFreshness';
import { refreshSurveillanceRouteListInBackground } from '../../../../gql/surveillanceRouteListRefresh';
import { refreshOrderFlags } from '../../../../gql/services/orderFlags';

/**
 * Identifica a ESTE loader ante el marcador de caducidad. Cada consumidor lleva
 * su propia cuenta, así que el listado histórico —que lee la misma query con
 * otros filtros— no se da por refrescado porque este haya ido al servidor.
 */
const FRESHNESS_CONSUMER = 'surveillanceRoute/generalView';

/**
 * Lee un catálogo de la caché de Apollo y NUNCA sale a la red.
 *
 * Los mismos catálogos se piden `cache-first` en el camino normal, que es lo
 * correcto ahí. En el camino de «servir la caché» no vale: un `cache-first` sin
 * el dato dispara una petición, y con cobertura degradada esa petición se
 * cuelga — exactamente el fallo que este camino viene a evitar.
 */
const readCatalogFromCache = <T,>(
  query: DocumentNode,
  field: string,
): T[] | null => {
  try {
    const data = client.readQuery({ query }) as Record<string, T[]> | null;
    return data?.[field] ?? null;
  } catch {
    return null;
  }
};

/**
 * Opciones de los filtros, solo de la caché.
 *
 * Lo que falte sale vacío y se pide **en segundo plano**, sin que nadie lo
 * espere: así el desplegable se repuebla en la siguiente entrada en vez de
 * quedarse vacío para siempre. En la práctica no falta nunca —los catálogos se
 * traen en la misma pasada y ANTES que la lista, así que tener la lista en
 * caché implica tenerlos a ellos—, pero un vacío silencioso y permanente es
 * peor que una petición de más.
 */
const readFilterCatalogsFromCache = () => {
  const entries: Array<[string, DocumentNode, string]> = [
    ['capitols', FETCH_CAPITOLS, 'capitols'],
    ['concessions', GET_CONCESSIONS, 'concessions'],
    ['subCapitols', FETCH_SUB_CAPITOLS, 'subCapitols'],
    ['classifications', FETCH_CLASSIFICATIONS, 'classifications'],
    ['teams', FETCH_TEAMS, 'teams'],
    ['predefinedOrders', FETCH_PREDEFINED_ORDERS, 'predefinedOrders'],
    ['roads', GET_ROADS, 'roads'],
    ['users', FETCH_USERS, 'users'],
  ];

  const options: Record<string, unknown[]> = {};
  entries.forEach(([key, query, field]) => {
    const cached = readCatalogFromCache(query, field);
    if (cached) {
      options[key] = cached;
      return;
    }
    options[key] = [];
    // Fuego y olvido: repuebla la caché para la próxima entrada. Nadie espera
    // a esto, así que no puede colgar la pantalla.
    client.query({ query, fetchPolicy: 'cache-first' }).catch(() => undefined);
  });
  return options as ReturnType<typeof emptyFilterCatalogs>;
};

/** Solo para tipar el retorno de arriba con la forma que espera la página. */
const emptyFilterCatalogs = () => ({
  capitols: [] as unknown[],
  concessions: [] as unknown[],
  subCapitols: [] as unknown[],
  classifications: [] as unknown[],
  teams: [] as unknown[],
  predefinedOrders: [] as unknown[],
  roads: [] as unknown[],
  users: [] as unknown[],
});

/**
 * Orders gemelas de cada recorrido (de ahí salen ROID, descripción, fecha final
 * y creador), solo de la caché.
 *
 * 🔴 Y lo que falte se pide **en segundo plano**, igual que los catálogos de
 * arriba. La primera versión no lo hacía y se justificaba diciendo que la fila
 * «cae a su ROID derivado, como ya hacía el selector» — pero no es lo mismo: el
 * selector iba a la red y solo caía al respaldo si fallaba, mientras que leer de
 * caché y callarse deja la fila degradada **para siempre**, porque el refresco
 * de segundo plano solo vuelve a pedir la LISTA, nunca las Orders.
 *
 * Y el caso es de lo más normal, no un borde: un recorrido entra en la lista
 * tras un refresco y su Order jamás se descargó en esta tablet. Esa fila se
 * quedaba sin descripción, sin fecha final y sin creador — con cobertura o sin
 * ella, porque nada volvía a pedirla.
 *
 * Va por el warm-up POR LOTES: las que falten en UNA petición, no una por fila.
 */
const readOrdersFromCache = (
  routes: SurveillanceRoute[],
): Record<string, SurveillanceRouteOrder> => {
  const byRouteId: Record<string, SurveillanceRouteOrder> = {};
  const missing: string[] = [];

  routes.forEach((route) => {
    const orderId = route.orderIDs?.[0];
    if (!orderId) return;
    try {
      const data = client.readQuery({
        query: GET_ORDER_BY_ID,
        variables: { id: orderId },
      }) as { order?: SurveillanceRouteOrder } | null;
      if (data?.order) {
        byRouteId[route.id] = data.order;
        return;
      }
    } catch {
      // Caché ilegible para esa Order: cuenta como que falta.
    }
    missing.push(orderId);
  });

  if (missing.length > 0) {
    // Fuego y olvido: repuebla la caché para la próxima entrada. Nadie espera a
    // esto, así que no puede colgar la tabla ni con la red muerta.
    warmupOrdersByIds(missing).catch(() => undefined);
  }

  return byRouteId;
};

export const LoaderSurveillanceRoutesGeneralView: (
  userData: AuthContextType | null
) => LoaderFunction = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  // Banderitas (spec 2026-10-22 § 7.2): en paralelo y sin esperar; nunca lanza.
  void refreshOrderFlags(userData);
  try {
    const cursorSR = getCursor(tableIds.SURVEILLANCE_ROUTES)(store.getState());
    const dispatch = store.dispatch;
    const roleActive = userData?.user?.user?.rolActive;

    // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
    // anterior interrumpido (React Router cancel, hot reload, throw mid-fetch).
    // Sin esto, si el run anterior abrió el flag y no llegó al cierre, este
    // run cae en HIT path y nunca lo cierra → tabla bloqueada permanentemente.
    dispatch(loadingData(tableIds.SURVEILLANCE_ROUTES, false));

    if (userData?.user) {
      // Cache-read-first pattern (same as WO loader)
      const readSurveillanceRoutes = client.readQuery({
        query: GET_SURVEILLANCE_ROUTES,
        variables: {
          // Revisión W5 (I2): solo el listado activo pinta la marca del turno.
          withShiftWarning: true,
          input: {
            take: cursorSR.take,
            cursor: cursorSR.cursor,
            sortField: cursorSR.sortField,
            orderBy: cursorSR.orderBy,
            name: cursorSR.routeName,
            roadId: cursorSR.roadId,
            concessionId: cursorSR.concessionId,
            state: cursorSR.state,
          states: cursorSR.states,
            fromDate: cursorSR.fromDate,
            toDate: cursorSR.toDate,
            teamId: cursorSR.teamId,
          teamIds: cursorSR.teamIds,
          operatorIds: cursorSR.operatorIds,
          userIds: cursorSR.userIds,
            capitolId: cursorSR.capitolId,
            subCapitolId: cursorSR.subCapitolId,
            predefinedOrderId: cursorSR.predefinedOrderId,
            indexExpedition: cursorSR.indexExpedition,
            indexType: cursorSR.indexType,
            pkInitKm: cursorSR.pkInitKm,
            pkInitMeter: cursorSR.pkInitMeter,
            pkEndKm: cursorSR.pkEndKm,
            pkEndMeter: cursorSR.pkEndMeter,
            description: cursorSR.description,
            // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
            onlyFlagged: cursorSR.onlyFlagged,
          },
        },
      });

      const pageInfo = readSurveillanceRoutes?.surveillanceRoutes?.pageInfo;
      const totalCount = readSurveillanceRoutes?.surveillanceRoutes?.totalCount;
      const isCacheEmpty = JSON.stringify(readSurveillanceRoutes) === '{}';

      const surveillanceRoutes =
        readSurveillanceRoutes &&
        !isCacheEmpty &&
        readSurveillanceRoutes?.surveillanceRoutes?.surveillanceRoutes;

      // ── Caducada PERO con lista en caché: servirla YA, sin una sola
      // petición, y refrescar por detrás (spec 2026-09-11) ──────────────────
      //
      // 🔴 Va ANTES de los `await` de abajo y no es un detalle de orden: los
      // catálogos de los filtros y las Orders gemelas se piden `cache-first`, y
      // un `cache-first` que no tiene el dato SALE A LA RED. Con cobertura
      // degradada esa petición se cuelga igual que la de la lista, así que
      // decidir después de esperarlas habría dejado el mismo spinner por la
      // puerta de atrás. Una Order gemela puede faltar de verdad: el borrado de
      // cachés derivadas (`evictDerivedCaches`) hace `gc()` y se lleva las que
      // solo colgaban de la lista de órdenes.
      //
      // Aquí se lee la caché y punto: lo que no esté, no está, y lo repone el
      // refresco de segundo plano. En la práctica está todo, porque la lista
      // solo puede estar cacheada si una visita anterior trajo también esto.
      const isListStale = isSurveillanceRouteListStale(FRESHNESS_CONSUMER);
      // Capturada ANTES de pedir: si una mutación caduca la lista mientras la
      // petición está en vuelo, su resultado no la incluye y no debe taparla.
      const versionBeforeFetch = currentSurveillanceRouteListVersion();

      // 🔴 Cualquier lista cacheada, caducada o no. Antes esto solo cubría la
      // rama caducada, y la otra seguía esperando a los catálogos y a las
      // Orders gemelas con `cache-first`: un miso ahí sale a la red y se
      // cuelga. Y es alcanzable — `evictDerivedCaches` hace `gc()` y se lleva
      // las Orders que solo colgaban de la lista de órdenes, así que basta con
      // que un refresco posterior deje la lista al día para caer justo ahí.
      if (surveillanceRoutes) {
        const rows = surveillanceRoutes as SurveillanceRoute[];
        if (isListStale) {
          refreshSurveillanceRouteListInBackground(
          () =>
            client.query({
              query: GET_SURVEILLANCE_ROUTES,
              variables: {
                // Revisión W5 (I2): solo el listado activo pinta la marca del turno.
                withShiftWarning: true,
                input: {
                  take: cursorSR.take,
                  cursor: cursorSR.cursor,
                  sortField: cursorSR.sortField,
                  orderBy: cursorSR.orderBy,
                  name: cursorSR.routeName,
                  roadId: cursorSR.roadId,
                  concessionId: cursorSR.concessionId,
                  state: cursorSR.state,
                  states: cursorSR.states,
                  fromDate: cursorSR.fromDate,
                  toDate: cursorSR.toDate,
                  teamId: cursorSR.teamId,
                  teamIds: cursorSR.teamIds,
                  operatorIds: cursorSR.operatorIds,
                  userIds: cursorSR.userIds,
                  capitolId: cursorSR.capitolId,
                  subCapitolId: cursorSR.subCapitolId,
                  predefinedOrderId: cursorSR.predefinedOrderId,
                  indexExpedition: cursorSR.indexExpedition,
                  indexType: cursorSR.indexType,
                  pkInitKm: cursorSR.pkInitKm,
                  pkInitMeter: cursorSR.pkInitMeter,
                  pkEndKm: cursorSR.pkEndKm,
                  pkEndMeter: cursorSR.pkEndMeter,
                  description: cursorSR.description,
                  onlyFlagged: cursorSR.onlyFlagged,
                },
              },
              // Las MISMAS variables que el `readQuery` de arriba: Apollo
              // particiona la lista por los `keyArgs` de `policies.ts`, así que
              // con otras escribiría en otra entrada y el lector no la vería
              // jamás (tripwire `misma-clave-de-cache`).
              fetchPolicy: 'network-only',
            }),
          {
            consumer: FRESHNESS_CONSUMER,
            versionBeforeFetch,
            // Los filtros con los que Apollo particiona la lista, MÁS la página
            // (`cursor`/`take`): el refresco de una no puede dar por servida a
            // otra. Sin el cursor, paginar con un refresco en vuelo se quedaba
            // sin pedir Y encima al día.
            key: JSON.stringify([
              cursorSR.take,
              cursorSR.cursor,
              cursorSR.sortField,
              cursorSR.orderBy,
              cursorSR.routeName,
              cursorSR.roadId,
              cursorSR.concessionId,
              cursorSR.state,
              cursorSR.states,
              cursorSR.fromDate,
              cursorSR.toDate,
              cursorSR.teamId,
              cursorSR.teamIds,
              cursorSR.operatorIds,
              cursorSR.userIds,
              cursorSR.capitolId,
              cursorSR.subCapitolId,
              cursorSR.predefinedOrderId,
              cursorSR.indexExpedition,
              cursorSR.indexType,
              cursorSR.pkInitKm,
              cursorSR.pkInitMeter,
              cursorSR.pkEndKm,
              cursorSR.pkEndMeter,
              cursorSR.description,
              cursorSR.onlyFlagged,
            ]),
            // Sin cobertura Apollo convierte el `network-only` en `cache-first`
            // y resuelve sin lanzar: eso NO es haber refrescado.
              reachedServer: () => !client.prioritizeCacheValues,
            },
          );
        }

        return {
          surveillanceRoutes: rows,
          filtersDataOptions: readFilterCatalogsFromCache(),
          ordersBySurveillanceRouteId: readOrdersFromCache(rows),
          totalCount,
          pageInfo,
          roleActive,
        };
      }

      // Fetch all filter data sources (V4 iter7: + roads para resolver
      // road.name en lugar de mostrar el Mongo ObjectId crudo).
      const [
        { data: { capitols } },
        { data: { concessions } },
        { data: { subCapitols } },
        { data: { classifications } },
        { data: { teams } },
        { data: { predefinedOrders } },
        { data: { roads } },
        { data: { users } },
      ] = await Promise.all([
        client.query({ query: FETCH_CAPITOLS, fetchPolicy: 'cache-first' }),
        client.query({ query: GET_CONCESSIONS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_SUB_CAPITOLS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_CLASSIFICATIONS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_TEAMS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_PREDEFINED_ORDERS, fetchPolicy: 'cache-first' }),
        client.query({ query: GET_ROADS, fetchPolicy: 'cache-first' }),
        // 2026-07-12: usuarios para el dropdown Operarios (showOperatorsFilter).
        client.query({ query: FETCH_USERS, fetchPolicy: 'cache-first' }),
      ]);

      const filtersDataOptions = {
        capitols,
        concessions,
        subCapitols,
        classifications,
        teams,
        predefinedOrders,
        roads,
        users,
      };

      // Patrón canónico entidad doble: cargar las Orders asociadas a cada SR
      // (description/notes/roid viven en la Order, no en el SR).
      const ordersBySurveillanceRouteId = await selectOrdersForSurveillanceRoutes(
        surveillanceRoutes || [],
      );

      const combinedData = {
        surveillanceRoutes,
        filtersDataOptions,
        ordersBySurveillanceRouteId,
        totalCount,
        pageInfo,
        roleActive,
      };

      if (!readSurveillanceRoutes || isCacheEmpty) {
        dispatch(loadingData(tableIds.SURVEILLANCE_ROUTES, true));
        try {
        let data;
        if (isCacheEmpty) {
          data = await client.query({
            query: GET_SURVEILLANCE_ROUTES,
            variables: {
              // Revisión W5 (I2): solo el listado activo pinta la marca del turno.
              withShiftWarning: true,
              input: {
                take: firstPageTake(cursorSR.take, takeBase),
                cursor: '',
                sortField: cursorSR.sortField,
                orderBy: cursorSR.orderBy ?? 'desc',
                name: cursorSR.routeName,
            roadId: cursorSR.roadId,
                concessionId: cursorSR.concessionId,
                state: cursorSR.state,
          states: cursorSR.states,
                fromDate: cursorSR.fromDate,
                toDate: cursorSR.toDate,
                teamId: cursorSR.teamId,
          teamIds: cursorSR.teamIds,
          operatorIds: cursorSR.operatorIds,
          userIds: cursorSR.userIds,
                capitolId: cursorSR.capitolId,
                subCapitolId: cursorSR.subCapitolId,
                    predefinedOrderId: cursorSR.predefinedOrderId,
                indexExpedition: cursorSR.indexExpedition,
                indexType: cursorSR.indexType,
                pkInitKm: cursorSR.pkInitKm,
                pkInitMeter: cursorSR.pkInitMeter,
                pkEndKm: cursorSR.pkEndKm,
                pkEndMeter: cursorSR.pkEndMeter,
                description: cursorSR.description,
                onlyFlagged: cursorSR.onlyFlagged,
                  },
            },
            fetchPolicy: 'cache-first',
          });

          const setCursorData = {
            take: firstPageTake(cursorSR.take, takeBase),
            cursor: '',
            // #74: sortField/orderBy se OMITEN (el merge del reducer preserva
            // los recién seteados por SortableHeader).
            routeName: cursorSR.routeName,
            roadId: cursorSR.roadId,
            concessionId: cursorSR.concessionId,
            state: cursorSR.state,
          states: cursorSR.states,
            fromDate: cursorSR.fromDate,
            toDate: cursorSR.toDate,
            teamId: cursorSR.teamId,
          teamIds: cursorSR.teamIds,
          operatorIds: cursorSR.operatorIds,
          userIds: cursorSR.userIds,
            capitolId: cursorSR.capitolId,
            subCapitolId: cursorSR.subCapitolId,
            predefinedOrderId: cursorSR.predefinedOrderId,
            indexExpedition: cursorSR.indexExpedition,
            indexType: cursorSR.indexType,
            pkInitKm: cursorSR.pkInitKm,
            pkInitMeter: cursorSR.pkInitMeter,
            pkEndKm: cursorSR.pkEndKm,
            pkEndMeter: cursorSR.pkEndMeter,
            description: cursorSR.description,
            onlyFlagged: cursorSR.onlyFlagged,
          };

          dispatch(setCursor(tableIds.SURVEILLANCE_ROUTES, setCursorData));
        } else {
          data = await client.query({
            query: GET_SURVEILLANCE_ROUTES,
            variables: {
              // Revisión W5 (I2): solo el listado activo pinta la marca del turno.
              withShiftWarning: true,
              input: {
                take: cursorSR.take,
                cursor: cursorSR.cursor,
                sortField: cursorSR.sortField,
                orderBy: cursorSR.orderBy,
                name: cursorSR.routeName,
            roadId: cursorSR.roadId,
                concessionId: cursorSR.concessionId,
                state: cursorSR.state,
          states: cursorSR.states,
                fromDate: cursorSR.fromDate,
                toDate: cursorSR.toDate,
                teamId: cursorSR.teamId,
          teamIds: cursorSR.teamIds,
          operatorIds: cursorSR.operatorIds,
          userIds: cursorSR.userIds,
                capitolId: cursorSR.capitolId,
                subCapitolId: cursorSR.subCapitolId,
                    predefinedOrderId: cursorSR.predefinedOrderId,
                indexExpedition: cursorSR.indexExpedition,
                indexType: cursorSR.indexType,
                pkInitKm: cursorSR.pkInitKm,
                pkInitMeter: cursorSR.pkInitMeter,
                pkEndKm: cursorSR.pkEndKm,
                pkEndMeter: cursorSR.pkEndMeter,
                description: cursorSR.description,
                onlyFlagged: cursorSR.onlyFlagged,
                  },
            },
            // Aquí ya NO hay lista que servir (el caso «caducada con caché»
            // salió antes, sirviéndola y refrescando por detrás). Queda el
            // caducado sin caché utilizable: `network-only` para no arriesgarse
            // a que Apollo resuelva de una entrada parcial y el refresco no
            // ocurra nunca.
            fetchPolicy: isListStale ? 'network-only' : 'cache-first',
          });
        }

        const loading = data.loading;
        const pageInfo = data.data.surveillanceRoutes.pageInfo;
        const totalCount = data.data.surveillanceRoutes.totalCount;
        const surveillanceRoutes = data.data.surveillanceRoutes.surveillanceRoutes;

        const ordersBySurveillanceRouteId =
          await selectOrdersForSurveillanceRoutes(surveillanceRoutes || []);

        const combinedData = {
          surveillanceRoutes,
          filtersDataOptions,
          ordersBySurveillanceRouteId,
          totalCount,
          pageInfo,
          loading,
          roleActive,
        };

        // Al día SOLO si la lectura llegó DE VERDAD al servidor.
        //
        // 🔴 Sin cobertura la app pone `client.prioritizeCacheValues = true`
        // (`gql/client.tsx`, listener de `offline`), y eso convierte el
        // `network-only` de arriba en `cache-first`: la promesa se resuelve con
        // lo cacheado y SIN lanzar. Si marcáramos al día ahí, daríamos por
        // refrescada una lista que no salió del dispositivo y no se reintentaría
        // al volver la cobertura. Medido: sin este guard, el reintento no ocurría.
        if (!client.prioritizeCacheValues) {
          markSurveillanceRouteListFresh(FRESHNESS_CONSUMER, versionBeforeFetch);
        }

        return combinedData;
        } catch (refreshError) {
          // Sin cobertura: se conserva lo que ya se estaba enseñando en vez de
          // vaciar la tabla. El marcador NO se toca, así que el próximo intento
          // volverá a pedirla. Si no había nada cacheado, se propaga al catch
          // exterior, que ya sabe devolver los filtros con la lista vacía.
          if (readSurveillanceRoutes && !isCacheEmpty) {
            console.log(
              '[SR listado] sin red al revalidar: se sirve la lista cacheada',
              refreshError,
            );
            return combinedData;
          }
          throw refreshError;
        } finally {
          // FIX (loader-loading-flag-leak): cierra el flag aunque el bloque MISS
          // lance una excepción a mitad (selector throw, query error). Antes,
          // si se lanzaba entre dispatch(true) y dispatch(false), el flag
          // quedaba fugado y la tabla bloqueada permanentemente.
          dispatch(loadingData(tableIds.SURVEILLANCE_ROUTES, false));
        }
      }

      return combinedData;
    }
    return {};
  } catch (err) {
    console.log('Loader error (SR query may have failed, filter data preserved):', err);
    // FIX (loader-loading-flag-leak): defensa en profundidad — si algo entre
    // el dispatch(false) inicial y el catch lanzó (p.ej. error en bootstrap
    // Promise.all antes del MISS branch), aseguramos que el flag esté cerrado.
    store.dispatch(loadingData(tableIds.SURVEILLANCE_ROUTES, false));

    // Try to load filter data even if the SR query failed
    let filtersDataOptionsFallback = {
      capitols: [] as any[],
      concessions: [] as any[],
      subCapitols: [] as any[],
      classifications: [] as any[],
      teams: [] as any[],
      predefinedOrders: [] as any[],
      roads: [] as any[],
      users: [] as any[],
    };
    try {
      const [
        { data: { capitols } },
        { data: { concessions } },
        { data: { subCapitols } },
        { data: { classifications } },
        { data: { teams } },
        { data: { predefinedOrders } },
        { data: { roads } },
        { data: { users } },
      ] = await Promise.all([
        client.query({ query: FETCH_CAPITOLS, fetchPolicy: 'cache-first' }),
        client.query({ query: GET_CONCESSIONS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_SUB_CAPITOLS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_CLASSIFICATIONS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_TEAMS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_PREDEFINED_ORDERS, fetchPolicy: 'cache-first' }),
        client.query({ query: GET_ROADS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_USERS, fetchPolicy: 'cache-first' }),
      ]);
      filtersDataOptionsFallback = { capitols, concessions, subCapitols, classifications, teams, predefinedOrders, roads, users };
    } catch (_) { /* filter queries also failed, use empty arrays */ }

    return {
        surveillanceRoutes: [],
        filtersDataOptions: filtersDataOptionsFallback,
        totalCount: 0,
        // V4 PR6 cleanup (2026-05-07): pageInfo alineado con refactor de main —
        // sin `lastCursor` (eliminado del schema GraphQL).
        pageInfo: { hasNextPage: false, hasPreviousPage: false },
        roleActive: userData?.user?.user?.rolActive,
      };
  }
};

  return { data: load() };
};

export default LoaderSurveillanceRoutesGeneralView;
